import { useState, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { useOrganization } from "@clerk/clerk-react";
import { Inbox, Search } from "lucide-react";
import { Layout } from "@/components/layout";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useEnhancedFilteredList } from "@/hooks/use-fuzzy-search";
import {
  usePcOrders,
  useMarkOrderReady,
  getPcOrderStatus,
  PcOrder,
} from "@/hooks/use-pc-orders";
import { PcOrderCard } from "@/components/pc/PcOrderCard";
import { PcOrderDetailDialog } from "@/components/pc/PcOrderDetailDialog";
import { useWillCallFlagState } from "@/hooks/use-will-call";

/**
 * Profit center order dashboard: incoming contractor orders for this PC,
 * with the ability to mark them ready for pickup. Mobile-first.
 *
 * Behind the "will-call" feature flag during rollout — when off, PC users are
 * sent to the new-order page (their landing route while the dashboard is off).
 */
export default function PcOrders() {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  // Raw flag state: undefined while PostHog loads, so enabled users aren't
  // bounced away during the load.
  const willCallFlag = useWillCallFlagState();

  useEffect(() => {
    if (willCallFlag === false) setLocation("/pc/orders/new");
  }, [willCallFlag, setLocation]);

  if (willCallFlag !== true) {
    return (
      <Layout
        headerContent={
          <span className="font-semibold">{t("pages.pcOrders.title")}</span>
        }
      >
        <div className="flex items-center justify-center min-h-[40vh]">
          <div className="h-8 w-48 bg-muted rounded animate-pulse" />
        </div>
      </Layout>
    );
  }

  return <PcOrdersDashboard />;
}

function PcOrdersDashboard() {
  const { t } = useTranslation();
  const { organization } = useOrganization();
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedOrder, setSelectedOrder] = useState<PcOrder | null>(null);
  const [markingOrderId, setMarkingOrderId] = useState<string | null>(null);

  const { orders, isLoading, error } = usePcOrders();
  const markOrderReadyMutation = useMarkOrderReady();

  const ordersWithStatus = useMemo(
    () =>
      orders.map((order) => ({ ...order, status: getPcOrderStatus(order) })),
    [orders],
  );

  const filterTabs = useMemo(
    () => [
      {
        label: t("pages.pcOrders.tabs.all"),
        value: "all",
        count: ordersWithStatus.length,
      },
      {
        label: t("pages.pcOrders.status.new"),
        value: "new",
        count: ordersWithStatus.filter((o) => o.status === "new").length,
      },
      {
        label: t("pages.pcOrders.status.readyForPickup"),
        value: "ready_for_pickup",
        count: ordersWithStatus.filter((o) => o.status === "ready_for_pickup")
          .length,
      },
    ],
    [t, ordersWithStatus],
  );

  const filteredOrders = useEnhancedFilteredList({
    items: ordersWithStatus,
    activeTab,
    searchQuery,
    statusKey: "status",
    searchKeys: [
      "contractor.name",
      "title",
      "createdByName",
      "products.description",
      "products.hajocaProductId",
    ],
    customSearchFn: (order, query) =>
      order.contractor.name.toLowerCase().includes(query) ||
      order.title.toLowerCase().includes(query) ||
      (order.createdByName?.toLowerCase().includes(query) ?? false),
  });

  const handleMarkReady = useCallback(
    async (order: PcOrder) => {
      try {
        setMarkingOrderId(order.uid);
        await markOrderReadyMutation.mutateAsync(order.uid);
      } catch (err) {
        toast({
          title: t("pages.pcOrders.markReadyFailedTitle"),
          description:
            err instanceof Error
              ? err.message
              : t("pages.pcOrders.markReadyFailedDescription"),
          variant: "destructive",
        });
      } finally {
        setMarkingOrderId(null);
      }
    },
    [markOrderReadyMutation, toast, t],
  );

  return (
    <Layout
      headerContent={
        <span className="font-semibold">{t("pages.pcOrders.title")}</span>
      }
    >
      <div className="space-y-6">
        <p className="text-muted-foreground">
          {t("pages.pcOrders.subtitle", {
            orgName: organization?.name ?? "",
            count: filteredOrders.length,
          })}
        </p>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full justify-evenly">
            {filterTabs.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value} className="flex-1">
                {tab.label}
                <span className="ml-1.5 text-xs text-muted-foreground">
                  {tab.count}
                </span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            type="text"
            placeholder={t("pages.pcOrders.searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 w-full h-12 text-base"
          />
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="bg-card p-6 rounded-lg border animate-pulse"
              >
                <div className="h-6 bg-muted rounded mb-3"></div>
                <div className="space-y-2 mb-4">
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                  <div className="h-4 bg-muted rounded w-1/2"></div>
                </div>
                <div className="h-10 bg-muted rounded"></div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-destructive">
              {t("pages.pcOrders.loadFailed")}
            </p>
          </div>
        ) : filteredOrders.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredOrders.map((order) => (
              <PcOrderCard
                key={order.uid}
                order={order}
                onView={setSelectedOrder}
                onMarkReady={handleMarkReady}
                isMarking={markingOrderId === order.uid}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="flex flex-col items-center">
              <Inbox className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="font-medium text-lg mb-2">
                {t("pages.pcOrders.noOrdersTitle")}
              </h3>
              <p className="text-muted-foreground">
                {t("pages.pcOrders.noOrdersDescription")}
              </p>
            </div>
          </div>
        )}

        <PcOrderDetailDialog
          order={selectedOrder}
          isOpen={!!selectedOrder}
          onClose={() => setSelectedOrder(null)}
        />
      </div>
    </Layout>
  );
}
